import { createFileRoute, Link } from "@tanstack/react-router";
import { Banknote, CheckCircle2, CreditCard, FileText, Plane } from "lucide-react";

import { Button } from "@/components/ui/button";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/rent-a-car-without-credit-card-bucharest";
const title = "Rent a Car Without a Credit Card in Bucharest";
const description =
  "Rent a car in Bucharest without a credit card: debit and cash accepted, no pre-authorisation, no deposit, free Otopeni delivery, insurance included.";

const faqs = [
  {
    q: "Can I rent a car in Bucharest with a debit card?",
    a: "Yes. We accept Visa and Mastercard debit cards, including digital banks such as Revolut. No amount is pre-authorised or blocked.",
  },
  {
    q: "Can I pay cash for a car rental in Romania?",
    a: "Yes. You can pay in cash in EUR or RON when you collect the car in Bucharest or at Otopeni Airport.",
  },
  {
    q: "Is a deposit taken instead of the credit card?",
    a: "No. There is no security deposit in any form — no card hold and no cash guarantee.",
  },
  {
    q: "Do you accept prepaid cards or cards from another person?",
    a: "Prepaid and digital-bank cards are accepted. The driver named in the contract must show their own licence and ID, but the payment card does not need a pre-authorisation.",
  },
];

export const Route = createFileRoute("/rent-a-car-without-credit-card-bucharest")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "rent a car without credit card bucharest, car rental no credit card romania, debit card car rental bucharest, cash car rental otopeni, no deposit no credit card car hire",
      },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Car rental without a credit card in Bucharest",
          serviceType: "Car rental accepting debit card and cash",
          provider: { "@id": "https://carentals.ro/#organization" },
          areaServed: { "@type": "City", name: "Bucharest", addressCountry: "RO" },
          url: CANONICAL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
    ],
  }),
  component: NoCreditCardPage,
});

const methods = [
  {
    icon: CreditCard,
    title: "Debit card",
    text: "Visa and Mastercard debit cards, including Revolut and other digital banks. No pre-authorisation, no card imprint.",
  },
  {
    icon: Banknote,
    title: "Cash",
    text: "Pay in EUR or RON at handover, in the city or at the airport. Receipt issued on the spot.",
  },
  {
    icon: FileText,
    title: "Bank transfer",
    text: "For companies and long-term rentals, with a proper invoice before or after the rental period.",
  },
];

function NoCreditCardPage() {
  return (
    <>
      <section className="bg-ink text-ink-foreground">
        <div className="container-page py-20">
          <p className="eyebrow">No credit card · No deposit · Bucharest</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.08] sm:text-5xl">
            No credit card? You can still drive away today
          </h1>

          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80 sm:text-lg">
            You do not need a credit card to rent a car with carentals.ro. We take no security
            deposit, so there is nothing to pre-authorise: pay by debit card, in cash or by bank
            transfer, with full insurance and the rovinieta already in the price.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Choose your car</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">
                WhatsApp us
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="max-w-3xl">
          <p className="eyebrow">Payment</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Ways you can pay</h2>
          <p className="mt-3 text-muted-foreground">
            Most rental companies in Romania demand a credit card only because they need to freeze a
            deposit on it. We removed the deposit, so the card requirement disappeared with it.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {methods.map((method) => (
            <div
              key={method.title}
              className="rounded-xl border border-border bg-card p-6 shadow-card"
            >
              <method.icon className="size-6 text-gold" />
              <h3 className="mt-4 font-display text-lg font-bold">{method.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{method.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Documents</p>
            <h2 className="mt-3 text-3xl font-bold">What you actually need</h2>
            <ul className="mt-6 space-y-3">
              {[
                "Driving licence held for at least 12 months (EU licences accepted as-is).",
                "Passport or national ID card.",
                "Minimum age 21, or 23 for the top of the luxury fleet.",
                "A phone number we can reach during the rental.",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-8 shadow-card">
            <Plane className="size-6 text-gold" />
            <h2 className="mt-4 font-display text-2xl font-bold">
              Otopeni Airport pickup, no card needed
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Send your flight number when booking. We track the flight and meet you in the
              short-term parking in front of Arrivals. Handover takes about ten minutes and payment
              is taken by debit card or cash on the spot. Airport delivery is free, day or night.
            </p>
            <Button asChild variant="gold" className="mt-6">
              <Link to="/rentals">Book airport delivery</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page pb-24">
        <div className="max-w-3xl">
          <p className="eyebrow">FAQ</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Renting without plastic</h2>
          <dl className="mt-8 space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="rounded-xl border border-border bg-card p-6">
                <dt className="flex gap-3 font-display text-base font-bold">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
                  {faq.q}
                </dt>
                <dd className="mt-2 pl-7 text-sm text-muted-foreground">{faq.a}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-8 text-sm text-muted-foreground">
            See also:{" "}
            <Link
              to="/car-rental-no-deposit-bucharest"
              className="font-semibold text-gold hover:underline"
            >
              car rental with no deposit in Bucharest
            </Link>{" "}
            and our{" "}
            <Link to="/blog" className="font-semibold text-gold hover:underline">
              guides to renting in Romania
            </Link>
            . Also useful:{" "}
            <Link to="/good-to-know" className="font-semibold text-gold hover:underline">
              good to know in Bucharest
            </Link>{" "}
            and{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "rent-a-car-without-credit-card-bucharest" }}
              className="font-semibold text-gold hover:underline"
            >
              renting without a credit card, step by step
            </Link>
            .
          </p>

        </div>
      </section>
    </>
  );
}
